import React, { useEffect } from 'react';
import { Form, Input, InputNumber, Modal, Select, TreeSelect } from '@maxtropy/components';
import { Space } from 'antd';
import { numberRule, scopeConfigs } from '../../config';
import { MediumType, ResilienceRecord, ScopeKey, SelectOption } from '../../types';
import styles from './index.module.scss';

interface ConfigModalProps {
  open: boolean;
  energy: MediumType;
  scope: ScopeKey;
  editingRecord?: ResilienceRecord;
  optionsByField?: Record<string, SelectOption[]>;
  confirmLoading?: boolean;
  onCancel: () => void;
  onSubmit: (values: Record<string, unknown>) => void;
}

const required = (message: string) => [{ required: true, message }];
const labelInValueFields = ['circuitKey', 'stationName', 'areaName', 'gasSources', 'compressorName'];

const getLabelValue = (record: ResilienceRecord, name: string) => {
  const value = record[name];
  if (Array.isArray(value)) {
    return value.map(item => {
      if (!item || typeof item !== 'object') return { label: String(item), value: item };

      const option = item as Record<string, unknown>;
      if ('label' in option && 'value' in option) return option;
      return {
        label: option.name ?? option.label ?? '',
        value: option.id ?? option.value ?? option.name,
      };
    });
  }
  if (value === undefined || value === null || typeof value === 'object') return value;

  if (name === 'circuitKey') {
    const label = [record.circuitName, record.circuitCode].filter(Boolean).join('/') || String(value);
    return { label, value };
  }

  return { label: String(value), value };
};

const getFormValues = (record: ResilienceRecord) =>
  labelInValueFields.reduce<Record<string, unknown>>(
    (values, name) => ({
      ...values,
      [name]: getLabelValue(record, name),
    }),
    { ...record }
  );

const getAreaIdsValue = (record: ResilienceRecord, options: SelectOption[] = []) => {
  const findOption = (items: SelectOption[], targetId: string | number): SelectOption | undefined => {
    for (const item of items) {
      if (item.value === targetId) return item;
      const child = findOption((item.children as SelectOption[] | undefined) ?? [], targetId);
      if (child) return child;
    }
  };

  return ((record.areaIds as Array<string | number> | undefined) ?? []).map(areaId => {
    const option = findOption(options, areaId);
    return { value: areaId, label: option?.label ?? String(areaId) };
  });
};

const ConfigModal: React.FC<ConfigModalProps> = ({
  open,
  energy,
  scope,
  editingRecord,
  optionsByField,
  confirmLoading,
  onCancel,
  onSubmit,
}) => {
  const [form] = Form.useForm();
  const disabledName = Boolean(editingRecord);
  const ratedFlowLabel = scopeConfigs[scope]?.fields.find(field => field.name === 'ratedFlow')?.label ?? '额定流量';
  useEffect(() => {
    if (!open) return;
    if (editingRecord) {
      form.setFieldsValue({
        ...getFormValues(editingRecord),
        ...(energy === MediumType.Electricity
          ? { areaIds: getAreaIdsValue(editingRecord, optionsByField?.areaIds) }
          : {}),
      });
    } else form.resetFields();
  }, [editingRecord, energy, form, open, optionsByField]);

  const handleOk = () => form.validateFields().then(onSubmit);
  const selectProps = (name: string, multiple = false) => ({
    allowClear: true,
    labelInValue: labelInValueFields.includes(name),
    showSearch: true,
    mode: multiple ? ('multiple' as const) : undefined,
    optionFilterProp: 'label',
    options: optionsByField?.[name],
    placeholder: '请选择',
  });
  const treeSelectProps = (name: string) => ({
    allowClear: true,
    labelInValue: true,
    showSearch: true,
    filterTreeNode: (search: string, node: { title?: unknown }) =>
      String(node.title ?? '')
        .toLowerCase()
        .includes(search.toLowerCase()),
    placeholder: '请选择',
    treeData: optionsByField?.[name],
  });
  const numberInput = (
    <Space.Compact className={styles.fieldWithUnit}>
      <Form.Item name="ratedFlow" noStyle rules={[...required(`请输入${ratedFlowLabel}`), numberRule]}>
        <InputNumber
          stringMode
          min={0}
          max={99999999.99}
          precision={2}
          placeholder="请输入"
          style={{ width: '100%' }}
        />
      </Form.Item>
      <span className={styles.unit}>Nm³/h</span>
    </Space.Compact>
  );

  return (
    <Modal
      title={editingRecord ? '编辑' : '新增'}
      open={open}
      onCancel={onCancel}
      onOk={handleOk}
      confirmLoading={confirmLoading}
      destroyOnClose
      contentClassName="modal-form-content"
    >
      <Form form={form}>
        {energy === MediumType.Electricity ? (
          <>
            <Form.Item name="circuitKey" label="回路名称" rules={required('请选择回路名称')}>
              <Select {...selectProps('circuitKey')} disabled={disabledName} />
            </Form.Item>
            <Form.Item name="circuitStart" label="回路起点" rules={required('请输入回路起点')}>
              <Input maxLength={50} placeholder="请输入" />
            </Form.Item>
            <Form.Item name="circuitEnd" label="回路终点" rules={required('请输入回路终点')}>
              <Input maxLength={50} placeholder="请输入" />
            </Form.Item>
            <Form.Item name="areaIds" label="关联区域">
              <TreeSelect {...treeSelectProps('areaIds')} treeCheckable treeCheckStrictly />
            </Form.Item>
            <Form.Item label="线路额定电流" required>
              <Space.Compact className={styles.fieldWithUnit}>
                <Form.Item name="ratedCurrent" noStyle rules={[...required('请输入线路额定电流'), numberRule]}>
                  <InputNumber
                    stringMode
                    min={0}
                    max={99999999.99}
                    precision={2}
                    placeholder="请输入"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
                <span className={styles.unit}>A</span>
              </Space.Compact>
            </Form.Item>
          </>
        ) : (
          <>
            {scope === ScopeKey.Station && (
              <Form.Item name="stationName" label="站房名称" rules={required('请选择站房名称')}>
                <TreeSelect {...treeSelectProps('stationName')} disabled={disabledName} />
              </Form.Item>
            )}
            {scope === ScopeKey.Area && (
              <Form.Item name="areaName" label="区域名称" rules={required('请选择区域名称')}>
                <TreeSelect {...treeSelectProps('areaName')} disabled={disabledName} />
              </Form.Item>
            )}
            {scope === ScopeKey.Outlet && (
              <Form.Item name="outletName" label="出口点位名称" rules={required('请输入出口点位名称')}>
                <Input maxLength={50} placeholder="请输入" disabled={disabledName} />
              </Form.Item>
            )}
            {scope === ScopeKey.Compressor && (
              <Form.Item name="compressorName" label="空压机名称" rules={required('请选择空压机名称')}>
                <Select {...selectProps('compressorName')} disabled={disabledName} />
              </Form.Item>
            )}
            {[ScopeKey.Area, ScopeKey.Outlet].includes(scope) && (
              <Form.Item name="gasSources" label="气体来源" rules={required('请选择气体来源')}>
                <TreeSelect {...treeSelectProps('gasSources')} multiple />
              </Form.Item>
            )}
            <Form.Item name="flowMeterDeviceIds" label="对应流量计" rules={required('请选择对应流量计')}>
              <Select {...selectProps('flowMeterDeviceIds', true)} />
            </Form.Item>
            <Form.Item label={ratedFlowLabel} required>
              {numberInput}
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  );
};

export default ConfigModal;
